/** Ignore UTF-8 BOM so the first header parses correctly. */
function stripBom(s: string): string {
  return s.replace(/^\uFEFF/, "");
}

function detectDelimiter(firstRow: string): string {
  let inQuotes = false;
  let semicolons = 0;
  let commas = 0;
  for (let i = 0; i < firstRow.length; i++) {
    const c = firstRow[i];
    if (c === '"') {
      if (inQuotes && i + 1 < firstRow.length && firstRow[i + 1] === '"') {
        i++;
        continue;
      }
      inQuotes = !inQuotes;
      continue;
    }
    if (!inQuotes) {
      if (c === ";") semicolons++;
      else if (c === ",") commas++;
    }
  }
  return semicolons >= commas ? ";" : ",";
}

/** Split on newlines only outside quoted fields (handles CRLF and quoted multiline cells). */
function splitCsvRows(text: string): string[] {
  const rows: string[] = [];
  let rowStart = 0;
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (c === '"') {
      if (inQuotes && i + 1 < text.length && text[i + 1] === '"') {
        i++;
        continue;
      }
      inQuotes = !inQuotes;
      continue;
    }
    if (!inQuotes && c === "\r") {
      rows.push(text.slice(rowStart, i));
      if (text[i + 1] === "\n") i++;
      rowStart = i + 1;
      continue;
    }
    if (!inQuotes && c === "\n") {
      rows.push(text.slice(rowStart, i));
      rowStart = i + 1;
    }
  }
  rows.push(text.slice(rowStart));
  return rows.filter((r) => r.trim().length > 0);
}

/**
 * Parse one CSV record (no unescaped record separators). Handles quoted fields,
 * commas/semicolons inside quotes, escaped quotes (""), and trimmed field values.
 */
function parseCsvLine(line: string, delimiter: string): string[] {
  const result: string[] = [];
  let current = "";
  let i = 0;
  let inQuotes = false;

  while (i < line.length) {
    const c = line[i];
    if (inQuotes) {
      if (c === '"') {
        if (i + 1 < line.length && line[i + 1] === '"') {
          current += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i += 1;
        continue;
      }
      current += c;
      i += 1;
      continue;
    }

    if (c === '"') {
      inQuotes = true;
      i += 1;
      continue;
    }

    if (c === delimiter) {
      result.push(current.trim());
      current = "";
      i += 1;
      continue;
    }

    if (c === " " || c === "\t") {
      let j = i;
      while (j < line.length && (line[j] === " " || line[j] === "\t")) j++;
      if (j >= line.length || line[j] === delimiter) {
        i = j;
        continue;
      }
      current += line.slice(i, j);
      i = j;
      continue;
    }

    current += c;
    i += 1;
  }

  result.push(current.trim());
  return result;
}

export function parseCsv(text: string): { headers: string[]; rows: string[][] } {
  const trimmed = stripBom(text.trim());
  if (!trimmed) return { headers: [], rows: [] };

  const physicalRows = splitCsvRows(trimmed);
  if (physicalRows.length === 0) return { headers: [], rows: [] };

  const delimiter = detectDelimiter(physicalRows[0]);
  const headers = parseCsvLine(physicalRows[0], delimiter);
  const rows = physicalRows.slice(1).map((line) => parseCsvLine(line, delimiter));
  return { headers, rows };
}
